/**
 * ORDER SIMILAR FEATURE FOR FILE CARDS
 */
import { SITE_DOMAIN } from '@/shared/config/constants';

/**
 * German citation style → string value mapping.
 * DE form uses string `paperFormat` param instead of numeric `citationStyle`.
 */
const DE_CITATION_MAP: Record<number, string> = {
  1: 'Deutsche',
  2: 'Amerikanische',
  3: 'Andere',
};

export interface OrderParams {
  workType?: number | null;
  academicLevel?: number | null;
  paperType?: number | null;
  citationStyle?: number | null;
  lineSpacing?: number | null; // EN only
  discipline?: number | null;
  spezifikation?: number | null; // DE only: 0 = Literatur, 1 = Empirisch
  pages?: number | null;
}

type ParamKey = keyof OrderParams;

/** Transform function for param values (e.g. DE citation style ID → string) */
type ParamTransform = (value: number) => string | undefined;

/** Mapping: OrderParams field → query param name, with optional value transform */
type ParamMapping = [field: ParamKey, queryKey: string, transform?: ParamTransform];

/** EN: /order — numeric IDs with `id` prefix */
const EN_PARAMS: ParamMapping[] = [
  ['workType', 'idWorkType'],
  ['academicLevel', 'idAcademicLevel'],
  ['paperType', 'idPaperType'],
  ['citationStyle', 'idCitationStyle'],
  ['lineSpacing', 'idLineSpacing'],
  ['discipline', 'idAcademicDiscipline'],
];

/** DE: /anfrage — different param names, string-based paperFormat */
const DE_PARAMS: ParamMapping[] = [
  ['workType', 'workType'],
  ['academicLevel', 'level'],
  ['citationStyle', 'paperFormat', (id) => DE_CITATION_MAP[id]],
  ['paperType', 'paperType'],
  ['discipline', 'subject'],
  ['spezifikation', 'spezifikationFeature'],
  ['pages', 'pages'],
];

/** Build query string from params using a mapping config */
const buildFromMapping = (params: OrderParams, mapping: ParamMapping[]): URLSearchParams => {
  const qs = new URLSearchParams();
  for (const [field, queryKey, transform] of mapping) {
    const raw = params[field];
    if (raw == null) continue;
    const value = transform ? transform(raw) : String(raw);
    if (value) qs.set(queryKey, value);
  }
  return qs;
};

const ORDER_CONFIG = {
  en: { path: '/order', params: EN_PARAMS },
  de: { path: '/anfrage', params: DE_PARAMS },
} as const;

type OrderLang = keyof typeof ORDER_CONFIG;

/** Base order page URL (clean, no query params) */
export const getOrderBaseUrl = (domain = SITE_DOMAIN, isGerman = false) =>
  `https://app.${domain}${ORDER_CONFIG[isGerman ? 'de' : 'en'].path}`;

/** Full order URL with pre-filled query params */
export const buildOrderUrl = (params: OrderParams, domain = SITE_DOMAIN, isGerman = false): string => {
  const lang: OrderLang = isGerman ? 'de' : 'en';
  const { path, params: mapping } = ORDER_CONFIG[lang];
  const qs = buildFromMapping(params, mapping).toString();
  return `https://app.${domain}${path}${qs ? `?${qs}` : ''}`;
};
